import React, { useState } from 'react';
import { AdvertisementItem, LanguageCode, SiteSettings } from '../types';
import {
  Building2,
  Phone,
  MessageCircle,
  MapPin,
  Globe,
  Mail,
  Calendar,
  Search,
  Tag,
  ExternalLink,
  Info,
  Megaphone,
  CheckCircle2,
  Clock,
  Eye,
  ChevronRight,
  X,
  Sparkles,
} from 'lucide-react';
import { recordAdViewApi } from '../services/api';

interface AdsViewProps {
  currentLang: LanguageCode;
  ads: AdvertisementItem[];
  settings: SiteSettings;
  onNavClick: (tab: string) => void;
}

export const AdsView: React.FC<AdsViewProps> = ({
  currentLang,
  ads,
  settings,
  onNavClick,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tümü');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedAd, setSelectedAd] = useState<AdvertisementItem | null>(null);

  // Filter only active ads for public view
  const activeAds = ads.filter((ad) => ad.isActive);

  // Extract categories
  const categories = [
    'Tümü',
    ...Array.from(new Set(activeAds.map((ad) => ad.category || 'Diğer'))),
  ];

  // Filtered ads list
  const filteredAds = activeAds.filter((ad) => {
    const matchesCategory =
      selectedCategory === 'Tümü' || ad.category === selectedCategory;
    const matchesSearch =
      ad.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.address.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleOpenDetail = (ad: AdvertisementItem) => {
    setSelectedAd(ad);
    // Record view count
    recordAdViewApi(ad.id);
  };

  const calculateDaysRemaining = (endDateStr: string) => {
    try {
      const end = new Date(endDateStr + 'T23:59:59').getTime();
      const now = Date.now();
      const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch {
      return 30;
    }
  };

  const adminWhatsapp = settings.whatsapp || '+33614629706';
  const cleanWhatsapp = adminWhatsapp.replace(/[^0-9]/g, '');

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8 animate-fadeIn">
      {/* 1. HERO BANNER */}
      <div className="relative rounded-3xl overflow-hidden glass-card border border-[#D4AF37]/30 bg-gradient-to-r from-[#0B1426] via-[#10241F] to-[#0B3D2E] p-8 md:p-12 shadow-2xl text-white">
        <div className="absolute -right-10 -bottom-10 w-80 h-80 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#F3E5AB] text-xs font-bold uppercase tracking-wider">
            <Megaphone className="w-4 h-4 text-[#D4AF37]" />
            <span>Firma & Şahıs Reklam Alanı</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading tracking-tight text-white leading-tight">
            Topluluk Rehberi & İşletme Reklamları
          </h1>
          <p className="text-gray-300 text-sm md:text-base leading-relaxed font-sans">
            Fransa ve çevresindeki dernek üyelerimize, yerel firma ve şahıslara ait hizmet ve ürün tanıtımları. Siz de firmanızı 1 aylık süreyle tanıtmak için dernek yönetimiyle iletişime geçebilirsiniz.
          </p>
          <div className="pt-2 flex flex-wrap items-center gap-3">
            <a
              href={`https://wa.me/${cleanWhatsapp}?text=Merhaba,%20dernek%20web%20sitesine%20reklam%20vermek%20istiyorum.`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-bold text-xs hover:shadow-[0_0_20px_rgba(37,211,102,0.6)] transition-all transform hover:-translate-y-0.5"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Reklam Vermek İstiyorum (WhatsApp)</span>
            </a>
            <button
              onClick={() => onNavClick('contact')}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-xs transition-all"
            >
              <Phone className="w-4 h-4" />
              <span>Yönetimle İletişime Geç</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. AUTOMATIC 1-MONTH PERIOD NOTICE */}
      <div className="glass-card rounded-2xl p-4 border border-[#38BDF8]/30 bg-[#0B1426]/80 flex items-start sm:items-center gap-3 text-xs text-gray-300">
        <Info className="w-5 h-5 text-[#38BDF8] flex-shrink-0 mt-0.5 sm:mt-0" />
        <div className="flex-1">
          <p className="font-semibold text-white">
            📢 Reklam Yayın ve Süre Kuralları:
          </p>
          <p className="text-gray-300 text-[11px] mt-0.5">
            Sitede yer alan reklamlar <strong>1 aylık (30 Gün)</strong> süreyle aktif olarak yayınlanmaktadır. Süre dolduğunda sistem tarafından otomatik olarak pasif konuma alınır. Reklamını tekrar aktifleştirmek veya yayın süresini uzatmak isteyen firma sahipleri dernek yönetimi ile iletişime geçebilir.
          </p>
        </div>
      </div>

      {/* 3. FILTER & SEARCH BAR */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Firma adı, sektör veya adres ara..."
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-[#0B1426]/90 border border-[#38BDF8]/30 text-white placeholder-gray-400 text-xs focus:outline-none focus:border-[#38BDF8] focus:ring-1 focus:ring-[#38BDF8] transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Categories */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-[#0B1426] shadow-[0_0_15px_rgba(212,175,55,0.5)] font-extrabold'
                  : 'bg-[#132238] text-gray-300 border border-[#38BDF8]/20 hover:border-[#38BDF8] hover:text-[#38BDF8]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* 4. ADS GRID */}
      {filteredAds.length === 0 ? (
        <div className="glass-card rounded-3xl p-12 text-center space-y-4 border border-white/10">
          <Building2 className="w-12 h-12 text-gray-500 mx-auto" />
          <h3 className="text-lg font-bold text-white">Reklam Bulunamadı</h3>
          <p className="text-xs text-gray-400 max-w-md mx-auto">
            Arama kriterlerinize uygun aktif bir reklam bulunmamaktadır. Firmanızı ilk ekleyen siz olabilirsiniz!
          </p>
          <a
            href={`https://wa.me/${cleanWhatsapp}?text=Merhaba,%20dernek%20web%20sitesine%20reklam%20vermek%20istiyorum.`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#25D366] text-white font-bold text-xs hover:bg-[#128C7E] transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Reklam Başvurusu Yap</span>
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAds.map((ad) => {
            const daysLeft = calculateDaysRemaining(ad.endDate);
            const adWhatsappClean = (ad.whatsapp || ad.phone || '').replace(/[^0-9]/g, '');

            return (
              <div
                key={ad.id}
                className="group glass-card rounded-3xl border border-[#D4AF37]/30 bg-[#0B1426]/90 overflow-hidden flex flex-col hover:border-[#D4AF37] hover:shadow-[0_0_25px_rgba(212,175,55,0.3)] transition-all duration-300"
              >
                {/* Banner / Image preview */}
                <div className="relative h-44 overflow-hidden bg-[#132238]">
                  <img
                    src={ad.image || ad.logo}
                    alt={ad.companyName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1426] via-transparent to-black/40" />

                  {/* Category Pill */}
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#0B1426]/80 backdrop-blur-md border border-[#D4AF37]/40 text-[#F3E5AB] text-[10px] font-bold">
                    {ad.category || 'İşletme'}
                  </div>

                  {/* 1 Month Badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-950/80 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold">
                    <Clock className="w-3 h-3 text-emerald-400" />
                    <span>{daysLeft} Gün Kaldı</span>
                  </div>

                  {/* Logo overlay */}
                  <div className="absolute -bottom-5 left-5 w-14 h-14 rounded-2xl bg-[#0B1426] border-2 border-[#D4AF37] p-0.5 overflow-hidden shadow-lg">
                    <img
                      src={ad.logo}
                      alt="Logo"
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                </div>

                {/* Content body */}
                <div className="p-5 pt-8 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-extrabold text-base text-white font-heading line-clamp-1 group-hover:text-[#D4AF37] transition-colors">
                        {ad.companyName}
                      </h3>
                    </div>

                    {ad.contactPerson && (
                      <p className="text-[11px] text-[#D4AF37] font-semibold">
                        Yetkili: {ad.contactPerson}
                      </p>
                    )}

                    <p className="text-xs text-gray-300 line-clamp-3 leading-relaxed">
                      {ad.summary}
                    </p>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-white/10">
                    {/* Address & Phone info */}
                    <div className="space-y-1.5 text-[11px] text-gray-300">
                      <div className="flex items-center gap-2 text-gray-300 truncate">
                        <MapPin className="w-3.5 h-3.5 text-[#38BDF8] flex-shrink-0" />
                        <span className="truncate">{ad.address}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone className="w-3.5 h-3.5 text-[#38BDF8] flex-shrink-0" />
                        <span>{ad.phone}</span>
                      </div>
                    </div>

                    {/* Quick action buttons */}
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {adWhatsappClean ? (
                        <a
                          href={`https://wa.me/${adWhatsappClean}?text=Merhaba,%20dernek%20web%20sitesindeki%20reklamınız%20üzerinden%20ulaşıyorum.`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#25D366] hover:bg-[#128C7E] text-white text-[11px] font-bold transition-all shadow-md"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          <span>WhatsApp</span>
                        </a>
                      ) : (
                        <a
                          href={`tel:${ad.phone}`}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#38BDF8] hover:bg-[#0284C7] text-[#0B1426] text-[11px] font-bold transition-all shadow-md"
                        >
                          <Phone className="w-3.5 h-3.5" />
                          <span>Ara</span>
                        </a>
                      )}

                      <button
                        onClick={() => handleOpenDetail(ad)}
                        className="flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-[#132238] border border-[#D4AF37]/40 hover:bg-[#D4AF37] hover:text-[#0B1426] text-white text-[11px] font-bold transition-all"
                      >
                        <span>Detaylar</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. REKLAM DETAY MODAL */}
      {selectedAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-card rounded-3xl border border-[#D4AF37] bg-[#0B1426] p-6 sm:p-8 space-y-6 text-white shadow-2xl">
            {/* Close button */}
            <button
              onClick={() => setSelectedAd(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="flex items-start gap-4">
              <img
                src={selectedAd.logo}
                alt={selectedAd.companyName}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 border-[#D4AF37] object-cover flex-shrink-0"
              />
              <div className="space-y-1">
                <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#F3E5AB] text-[10px] font-bold uppercase">
                  {selectedAd.category || 'İşletme'}
                </span>
                <h2 className="text-xl sm:text-2xl font-black font-heading text-white">
                  {selectedAd.companyName}
                </h2>
                {selectedAd.contactPerson && (
                  <p className="text-xs text-[#D4AF37] font-semibold">
                    Yetkili: {selectedAd.contactPerson}
                  </p>
                )}
              </div>
            </div>

            {/* Banner Image */}
            {selectedAd.image && (
              <div className="rounded-2xl overflow-hidden aspect-video bg-[#132238] border border-white/10">
                <img
                  src={selectedAd.image}
                  alt={selectedAd.companyName}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Publication Info Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3.5 rounded-2xl bg-[#132238] border border-white/10 text-xs text-gray-300">
              <div>
                <span className="text-[10px] text-gray-400 block">Yayın Başlangıcı:</span>
                <span className="font-bold text-white">{selectedAd.startDate}</span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block">Yayın Bitişi (1 Ay):</span>
                <span className="font-bold text-[#D4AF37]">{selectedAd.endDate}</span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block">Görüntülenme:</span>
                <span className="font-bold text-[#38BDF8] flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" />
                  {selectedAd.viewCount || 1} Kez
                </span>
              </div>
            </div>

            {/* Content text */}
            <div className="space-y-2">
              <h4 className="font-bold text-sm text-[#D4AF37] uppercase tracking-wider">
                Reklam Detayı & Açıklama
              </h4>
              <p className="text-xs text-gray-200 leading-relaxed whitespace-pre-line font-sans">
                {selectedAd.content || selectedAd.summary}
              </p>
            </div>

            {/* Contact Details Card */}
            <div className="p-4 rounded-2xl bg-[#132238]/80 border border-[#38BDF8]/30 space-y-3 text-xs">
              <h4 className="font-bold text-xs text-[#38BDF8] uppercase tracking-wider">
                İletişim & Adres Bilgileri
              </h4>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#38BDF8] flex-shrink-0 mt-0.5" />
                  <span>{selectedAd.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#38BDF8] flex-shrink-0" />
                  <a href={`tel:${selectedAd.phone}`} className="hover:underline font-bold text-white">
                    {selectedAd.phone}
                  </a>
                </div>
                {selectedAd.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#38BDF8] flex-shrink-0" />
                    <span>{selectedAd.email}</span>
                  </div>
                )}
                {selectedAd.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#38BDF8] flex-shrink-0" />
                    <a
                      href={selectedAd.website.startsWith('http') ? selectedAd.website : `https://${selectedAd.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#38BDF8] hover:underline flex items-center gap-1 font-semibold"
                    >
                      <span>{selectedAd.website}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a
                href={`https://wa.me/${(selectedAd.whatsapp || selectedAd.phone).replace(/[^0-9]/g, '')}?text=Merhaba,%20dernek%20web%20sitesindeki%20reklamınız%20üzerinden%20ulaşıyorum.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-xs transition-all shadow-lg"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Direct WhatsApp İletişimi</span>
              </a>

              <a
                href={`tel:${selectedAd.phone}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#38BDF8] hover:bg-[#0284C7] text-[#0B1426] font-bold text-xs transition-all"
              >
                <Phone className="w-4 h-4" />
                <span>Hemen Ara</span>
              </a>

              <button
                onClick={() => setSelectedAd(null)}
                className="px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
